import React from 'react';

import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import { useRegions } from 'services/Data/RegionService';
import { translate } from '@acnb/CustomComponents/DropdownItem/Translate';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { usePatchMerchandisingProgramQuoteChangeShipping } from 'services/Data/MerchandisingProgramQuoteService';

const MerchandisingProgramQuoteDeliveryCheckoutSection = ({
  merchandisingProgramQuote,
  isLoading,
  validator,
  isDisabled,
  actionBtns,
}) => {
  const { t } = useTranslation();

  const { mutate: changeShipping, isPending: changingShipping } = usePatchMerchandisingProgramQuoteChangeShipping();
  const { data: regions, isLoading: gettingRegions } = useRegions();

  return (
    <SkeletonWrapper
      isLoading={isLoading || changingShipping || gettingRegions}
      component={
        <GridContainer>
          <GridItem xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={({ target: { checked } }) =>
                    changeShipping({ ...merchandisingProgramQuote, isDealerShippingAddress: checked })
                  }
                  checked={merchandisingProgramQuote?.isDealerShippingAddress || false}
                  disabled={!merchandisingProgramQuote || isDisabled || merchandisingProgramQuote.isDemo}
                />
              }
              label={t('copyConfiguredAddress')}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomInput
              labelText={t('address')}
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('address'), merchandisingProgramQuote?.shippingAddress, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...merchandisingProgramQuote, shippingAddress: value });
                },
                disabled: !merchandisingProgramQuote || isDisabled || merchandisingProgramQuote.isDealerShippingAddress,
                defaultValue: merchandisingProgramQuote?.shippingAddress ?? '',
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('city')}
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('city'), merchandisingProgramQuote?.shippingCity, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...merchandisingProgramQuote, shippingCity: value });
                },
                disabled: !merchandisingProgramQuote || isDisabled || merchandisingProgramQuote.isDealerShippingAddress,
                defaultValue: merchandisingProgramQuote?.shippingCity ?? '',
              }}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomAutocomplete
              labelText={t('regions')}
              options={regions || []}
              formControlProps={{
                fullWidth: true,
              }}
              disabled={!merchandisingProgramQuote || isDisabled || merchandisingProgramQuote.isDealerShippingAddress}
              error={validator.current.message(t('region'), merchandisingProgramQuote?.shippingRegion_Id, `required`)}
              autocompleteProps={{
                onChange: (event, value) => {
                  changeShipping({ ...merchandisingProgramQuote, shippingRegion_Id: value.id });
                },
                disableClearable: true,
                isOptionEqualToValue: (option, value) => option?.id === value,
                value: merchandisingProgramQuote?.shippingRegion_Id || null,
                getOptionLabel: option => {
                  let item = regions?.find(se => se?.id === option) || option;

                  return (
                    (item &&
                      `${translate({
                        label: item?.label,
                        t,
                      })}`) ||
                    ''
                  );
                },
                filterOptions: (options, state) => {
                  var searchTerms = state.inputValue.toLowerCase().split(' ');
                  var filteredOptions = options.filter(option =>
                    searchTerms.every(searchTerm =>
                      translate({ label: option.label, t })
                        ?.toLowerCase()
                        .includes(searchTerm),
                    ),
                  );

                  return filteredOptions;
                },
                renderOption: (props, option, { inputValue }) => {
                  const result = getHighlightParts(`${translate({ label: option.label, t })}`, inputValue);

                  return (
                    <div {...props} key={option.id}>
                      {result}
                    </div>
                  );
                },
                renderInput: params => (
                  <TextField
                    {...params}
                    label={t('regions')}
                    variant="outlined"
                    error={
                      !!validator.current.message(t('region'), merchandisingProgramQuote?.shippingRegion_Id, 'required')
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                      },
                    }}
                  />
                ),
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('postalCode')}
              id="shippingPostalCode"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('postalCode'), merchandisingProgramQuote?.shippingPostalCode, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...merchandisingProgramQuote, shippingPostalCode: value });
                },
                disabled: !merchandisingProgramQuote || isDisabled || merchandisingProgramQuote.isDealerShippingAddress,
                defaultValue: merchandisingProgramQuote?.shippingPostalCode ?? '',
              }}
            />
          </GridItem>
        </GridContainer>
      }
    />
  );
};

export default MerchandisingProgramQuoteDeliveryCheckoutSection;
